import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import {
  Text, Spinner, Badge, Button, Input, Dropdown, Option, MessageBar, MessageBarBody, Field,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDevices, createDevice, rotateDeviceKey,
  DEVICE_TYPES, DEVICE_BINDING_TYPES, type Device, type DeviceType, type DeviceStatus, type DeviceBindingType,
} from '../api/devices.api';
import { fetchShipments } from '../api/transport.api';
import { fetchExportShipments } from '../api/export.api';
import { fetchModules } from '../api/modules.api';
import { orderedModules } from '../utils/module-tree';
import { fetchLotes } from '../api/lotes.api';
import { onlyCampoLotes } from '../utils/lote-uso';
import { SearchableSelect, type SearchableSelectOption } from '../components/shared/SearchableSelect';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

/**
 * F5 — `/dispositivos` (ADMIN).
 *
 * Página operativa interna, fuera del portal inversionista. Existe porque la
 * activación de F5/F6 es por configuración: si un tenant enciende el tracking,
 * alguien tiene que poder registrar el dispositivo y ver su API key, y esa key
 * se muestra UNA sola vez.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: tokens.spacingHorizontalM },
  empty:   { padding: '14px 16px', border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  form:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '320px' },
  keyBox:  { fontFamily: tokens.fontFamilyMonospace, padding: tokens.spacingVerticalS, backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, wordBreak: 'break-all' },
  warn:    { color: tokens.colorPaletteDarkOrangeForeground1, fontSize: tokens.fontSizeBase200 },
});

/**
 * Semáforo de vida del dispositivo. Un device que no reporta en 24 h está roto,
 * sin batería o desconectado — y es justo lo que nadie nota hasta que hace falta
 * el dato.
 */
function freshness(lastSeenAt?: string): 'success' | 'warning' | 'danger' {
  if (!lastSeenAt) return 'danger';
  const hours = (Date.now() - Date.parse(lastSeenAt)) / 3_600_000;
  if (hours < 1) return 'success';
  if (hours < 24) return 'warning';
  return 'danger';
}

export function DevicesPage() {
  const s = useStyles();
  const t = useT();
  const { fmtDateTime } = useIntlFormat();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const fe = useFieldErrors();
  const [deviceType, setDeviceType] = useState<DeviceType>('THERMOGRAPH');
  // F5 Task 8 dio de alta el device siempre en bindingType NONE — sin binding,
  // ingest.handler.ts lo rechaza para siempre con DEVICE_NOT_BOUND (409). Estos
  // dos campos destraban el flujo end-to-end desde el alta misma.
  const [bindingType, setBindingType] = useState<DeviceBindingType>('NONE');
  const [bindingId, setBindingId] = useState('');
  // el error del servidor se limpia al reabrir el diálogo (ver useResetOnOpen)
  const [issuedKey, setIssuedKey] = useState<string | null>(null);

  useResetOnOpen(addOpen, () => {
    setName(''); setDeviceType('THERMOGRAPH'); setBindingType('NONE'); setBindingId('');
    create.reset();
  });

  const { data: devices, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
  });

  // Opciones reales del destino según el tipo de binding (solo se piden con el diálogo abierto).
  const needsTargets = addOpen && bindingType !== 'NONE';
  const { data: targetOptions = [] } = useQuery<SearchableSelectOption[]>({
    queryKey: ['device-binding-targets', bindingType],
    enabled: needsTargets,
    queryFn: async () => {
      if (bindingType === 'SHIPMENT') {
        const [dom, exp] = await Promise.all([
          fetchShipments({ limit: 100 }).catch(() => ({ shipments: [] as any[] })),
          fetchExportShipments(undefined, 100).catch(() => ({ shipments: [] as any[] })),
        ]);
        const seen = new Set<string>();
        return [...dom.shipments, ...exp.shipments].flatMap((sh: any) => {
          if (seen.has(sh.shipmentId)) return [];
          seen.add(sh.shipmentId);
          const detail = sh.containerCode ?? sh.containerId ?? sh.destination ?? sh.destinationCountry ?? sh.product;
          return [{ id: sh.shipmentId, label: detail ? `${sh.shipmentId} · ${detail}` : sh.shipmentId }];
        });
      }
      if (bindingType === 'MODULE') {
        return orderedModules(await fetchModules()).map(({ mod: m, depth }) => ({ id: m.moduleId, label: (depth ? '↳ ' : '') + (m.name ?? m.moduleId) }));
      }
      // Solo lotes de Campo: los administrativos no se asignan a dispositivos.
      return onlyCampoLotes(await fetchLotes()).map(l => ({ id: l.loteId, label: l.name ?? l.loteId }));
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['devices'] });

  const create = useMutation({
    mutationFn: () => createDevice({
      name,
      deviceType,
      capabilities: [],
      bindingType,
      bindingId: bindingType === 'NONE' ? undefined : bindingId.trim(),
    }),
    onSuccess: ({ apiKey }) => {
      setIssuedKey(apiKey);
      setAddOpen(false);
      setName('');
      setBindingType('NONE');
      setBindingId('');
      refresh();
    },
  });

  const rotate = useMutation({
    mutationFn: (deviceId: string) => rotateDeviceKey(deviceId),
    onSuccess: ({ apiKey }) => { setIssuedKey(apiKey); refresh(); },
  });

  const onRotate = (device: Device) => {
    if (window.confirm(t.devices.rotateConfirm)) rotate.mutate(device.deviceId);
  };

  // TypeScript no puede validar el acceso indexado `t.devices[`type${x}`]` contra
  // el tipo `Translations` (union de literales) — se tipa explícitamente el
  // diccionario como Record<string, string> para las claves dinámicas type*/status*.
  const typeLabel = (type: DeviceType) =>
    (t.devices as Record<string, string>)[`type${type}`] ?? type;
  const statusLabel = (status: DeviceStatus) =>
    (t.devices as Record<string, string>)[`status${status}`] ?? status;
  // Sin claves i18n dedicadas por tipo de binding (fuera de alcance agregarlas
  // aquí) — igual que la tabla ya muestra el bindingType crudo en la columna
  // "Asignado a" (`${device.bindingType} · ${device.bindingId}`), NONE es el
  // único caso con label traducida.
  const bindingTypeLabel = (bt: DeviceBindingType) => (bt === 'NONE' ? t.devices.unbound : bt);

  if (isLoading) return <Spinner />;

  return (
    <div className={s.root}>
      <div className={s.header}>
        <div>
          <PageTitle>{t.devices.title}</PageTitle>
          <br />
          <Text size={200}>{t.devices.subtitle}</Text>
        </div>
        <Button appearance="primary" onClick={() => { fe.clear(); setAddOpen(true); }}>{t.devices.add}</Button>
      </div>

      {!devices?.length ? (
        <div className={s.empty}>{t.devices.empty}</div>
      ) : (
        <DataTable
          rows={devices}
          rowKey={(d) => d.deviceId}
          columns={[
            { key: 'name', header: t.devices.name, render: (d) => d.name },
            { key: 'type', header: t.devices.type, render: (d) => typeLabel(d.deviceType) },
            { key: 'binding', header: t.devices.binding, render: (d) => d.bindingType === 'NONE' || !d.bindingId
              ? <Text size={200} italic>{t.devices.unbound}</Text>
              : `${d.bindingType} · ${d.bindingId}` },
            { key: 'prefix', header: t.devices.apiKeyPrefix, render: (d) => <code>{d.apiKeyPrefix}…</code> },
            { key: 'status', header: t.devices.status, render: (d) => (
              <Badge appearance="filled" color={d.status === 'ACTIVE' ? 'success' : 'subtle'}>{statusLabel(d.status)}</Badge>
            ) },
            { key: 'seen', header: t.devices.lastSeen, width: 1.6, render: (d) => (
              <>
                <Badge appearance="filled" color={freshness(d.lastSeenAt)}>
                  {d.lastSeenAt ? fmtDateTime(d.lastSeenAt) : t.devices.never}
                </Badge>
                {d.lastReadingSummary && <> <Text size={200}>{d.lastReadingSummary}</Text></>}
              </>
            ) },
            { key: 'actions', header: '', render: (d) => <Button size="small" onClick={() => onRotate(d)}>{t.devices.rotateKey}</Button> },
          ]}
        />
      )}

      <Dialog open={addOpen} onOpenChange={(_, d) => setAddOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.devices.add}</DialogTitle>
            <DialogContent>
              <div className={s.form}>
                <Field {...fe.props('name')}>
                  <Input
                    placeholder={t.devices.name}
                    value={name}
                    onChange={(_, d) => { setName(d.value); fe.clear('name'); }}
                  />
                </Field>
                <Dropdown
                  value={typeLabel(deviceType)}
                  selectedOptions={[deviceType]}
                  onOptionSelect={(_, d) => setDeviceType(d.optionValue as DeviceType)}
                >
                  {DEVICE_TYPES.map(type => (
                    <Option key={type} value={type}>{typeLabel(type)}</Option>
                  ))}
                </Dropdown>
                <Dropdown
                  placeholder={t.devices.binding}
                  value={bindingTypeLabel(bindingType)}
                  selectedOptions={[bindingType]}
                  onOptionSelect={(_, d) => {
                    const next = d.optionValue as DeviceBindingType;
                    setBindingType(next);
                    setBindingId(''); fe.clear('bindingId');
                  }}
                >
                  {DEVICE_BINDING_TYPES.map(bt => (
                    <Option key={bt} value={bt}>{bindingTypeLabel(bt)}</Option>
                  ))}
                </Dropdown>
                {bindingType !== 'NONE' && (
                  <SearchableSelect
                    placeholder={t.devices.bindingTargetPlaceholder}
                    options={targetOptions}
                    value={bindingId}
                    onChange={(id) => { setBindingId(id); fe.clear('bindingId'); }}
                    error={fe.errors.bindingId}
                  />
                )}
                {create.isError && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      {(create.error as any)?.response?.data?.error ?? (create.error as Error)?.message}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAddOpen(false)}>{t.devices.cancel}</Button>
              <Button
                appearance="primary"
                disabled={create.isPending}
                onClick={() => {
                  const req = t.common.validation.fieldRequired;
                  const ok = fe.validate({
                    name: !name.trim() && req,
                    bindingId: bindingType !== 'NONE' && !bindingId.trim() && req,
                  });
                  if (ok) create.mutate();
                }}
              >
                {t.devices.save}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* La key sale UNA vez: este diálogo es la única oportunidad de copiarla. */}
      <Dialog open={issuedKey !== null} onOpenChange={() => setIssuedKey(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.devices.apiKeyPrefix}</DialogTitle>
            <DialogContent>
              <div className={s.form}>
                <Text className={s.warn}>{t.devices.keyShownOnce}</Text>
                <div className={s.keyBox}>{issuedKey}</div>
                <Button onClick={() => issuedKey && navigator.clipboard.writeText(issuedKey)}>
                  {t.devices.copy}
                </Button>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setIssuedKey(null)}>{t.devices.cancel}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
